import React from 'react';
import { Bar } from 'react-chartjs-2';
import { NonBasicCardStyling } from './util';
import { type PreviewEyeProps } from './preview_eye';
import EmptyState from 'shared/empty_state';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';

export type DocumentStatsCardProps = {
  documentDownloadsByMonth: Record<string, number>;
  documentViewsByMonth: Record<string, number>;
  preview: boolean;
  previewEye: PreviewEyeProps;
  onPreviewChange: (isHidden: boolean) => void;
}

export default function DocumentStatsCard(props: DocumentStatsCardProps) {
  const keys = Object.keys({ ...(props.documentViewsByMonth || {}), ...(props.documentDownloadsByMonth || {}) }).sort()
  const labels: string[] = keys.map(k => {
    const year = Number(k.slice(0, 4))
    const monthIndex = Number(k.slice(4, 6)) - 1
    return new Date(year, monthIndex, 1).toLocaleString(undefined, { month: 'short' })
  })
  const viewsData = keys.map(k => props.documentViewsByMonth?.[k] || 0)
  const downloadsData = keys.map(k => props.documentDownloadsByMonth?.[k] || 0)

  const hasAnyData = viewsData.some(v => v > 0) || downloadsData.some(v => v > 0)

  const barData = {
    labels,
    datasets: [
      {
        label: 'Views',
        data: viewsData,
        backgroundColor: '#213c88'
      },
      {
        label: 'Downloads',
        data: downloadsData,
        backgroundColor: '#2a68e7'
      }
    ]
  }

  const barOptions = {
    hover: { mode: 'index', intersect: false },
    legend: { display: true, position: 'top' },
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      xAxes: [
        {
          stacked: false,
          ticks: { fontColor: '#8E93A4', fontSize: 9, maxRotation: 0, minRotation: 0 },
          gridLines: { color: '#D8DADF', display: false }
        }
      ],
      yAxes: [
        {
          stacked: false,
          ticks: { beginAtZero: true, fontColor: '#8E93A4', maxTicksLimit: 7, padding: 10 },
          gridLines: { color: '#D8DADF', drawBorder: false, drawTicks: false, zeroLineColor: '#D8DADF' }
        }
      ]
    },
    tooltips: {
      enabled: true,
      mode: 'index',
      intersect: false,
      displayColors: true,
      backgroundColor: '#FFFFFF',
      titleFontColor: '#2B2F3A',
      bodyFontColor: '#2B2F3A',
      borderColor: '#D8DADF',
      borderWidth: 1,
      xPadding: 10,
      yPadding: 10,
      callbacks: {
        label: function(tooltipItem: any, data: any) {
          const ds = data.datasets[tooltipItem.datasetIndex]
          const raw = ds && ds.data ? ds.data[tooltipItem.index] : 0
          const value = typeof raw === 'number' ? raw : Number(raw || 0)
          return `${ds.label}  ${value.toLocaleString()}`
        }
      }
    }
  }

  return (
    <div className={NonBasicCardStyling}>
      <div className="d-flex justify-content-between align-items-center">
        <h4 className="m-0 fw-normal">Document Stats</h4>
        { !props.preview && props.previewEye }
      </div>
      {hasAnyData ? (
        <div style={{ width: '100%', height: '90%' }}>
          <Bar data={barData} options={barOptions as any} />
        </div>
      ) : (
        <EmptyState
          bodyText="Waiting For Data..."
          header="No document stats data to display"
          icon={faExclamationCircle}
          iconSize="3x"
          style={{ minHeight: '250px' }}
        />
      )}
    </div>
  );
}